import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { buildStudentLoginPdf, rasterizeLogo } from '../../lib/studentLoginCards';
import logoMark from '../../assets/Logo.svg';
import './DashboardSchoolGrades.css';

const DashboardSchoolGrades = ({ grades, gradeSections, dataLoading, onRefresh, readOnly = false }) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingGrade, setEditingGrade] = useState(null);
    const [gradeFormData, setGradeFormData] = useState({ name: '', order_index: 0, is_active: true, level: 1 });
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [pendingCloseAction, setPendingCloseAction] = useState(null);
    const [initialFormData, setInitialFormData] = useState(null);

    // Subsections management state
    const [showSubsectionsModal, setShowSubsectionsModal] = useState(false);
    const [selectedGradeForSections, setSelectedGradeForSections] = useState(null);
    const [showAddSectionModal, setShowAddSectionModal] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [sectionFormData, setSectionFormData] = useState({ name: '', order_index: 0, is_active: true });
    const [showDiscardSectionConfirm, setShowDiscardSectionConfirm] = useState(false);
    const [pendingSectionCloseAction, setPendingSectionCloseAction] = useState(null);
    const [initialSectionFormData, setInitialSectionFormData] = useState(null);

    // Delete confirmation state
    const [showDeleteGradeConfirm, setShowDeleteGradeConfirm] = useState(false);
    const [gradeToDelete, setGradeToDelete] = useState(null);
    const [showDeleteSectionConfirm, setShowDeleteSectionConfirm] = useState(false);
    const [sectionToDelete, setSectionToDelete] = useState(null);

    // Students-in-grade modal state
    const [showStudentsModal, setShowStudentsModal] = useState(false);
    const [selectedGradeForStudents, setSelectedGradeForStudents] = useState(null);
    const [studentsInGrade, setStudentsInGrade] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [studentsSearch, setStudentsSearch] = useState('');
    const [pdfGenerating, setPdfGenerating] = useState(false);

    const schoolId = localStorage.getItem('school_id');

    // If the dashboard enters read-only mode, force-disable any destructive UX paths.
    useEffect(() => {
        if (!readOnly) return;
        setShowAddModal(false);
        setEditingGrade(null);
        setShowSubsectionsModal(false);
        setShowAddSectionModal(false);
        setEditingSection(null);
    }, [readOnly]);

    // Set initial form data when modal opens
    useEffect(() => {
        if (showAddModal) {
            setInitialFormData(JSON.parse(JSON.stringify(gradeFormData)));
        } else {
            setInitialFormData(null);
        }
    }, [showAddModal]);

    // Set initial section form data when section modal opens
    useEffect(() => {
        if (showAddSectionModal) {
            setInitialSectionFormData(JSON.parse(JSON.stringify(sectionFormData)));
        } else {
            setInitialSectionFormData(null);
        }
    }, [showAddSectionModal]);

    // Check if form has been modified
    const hasFormChanges = () => {
        if (!initialFormData) return false;
        return JSON.stringify(gradeFormData) !== JSON.stringify(initialFormData);
    };

    // Check if section form has been modified
    const hasSectionFormChanges = () => {
        if (!initialSectionFormData) return false;
        return JSON.stringify(sectionFormData) !== JSON.stringify(initialSectionFormData);
    };

    // Handle overlay click with unsaved changes check
    const handleOverlayClick = (closeAction) => {
        if (hasFormChanges()) {
            setPendingCloseAction(() => closeAction);
            setShowDiscardConfirm(true);
        } else {
            closeAction();
        }
    };

    // Handle section modal overlay click with unsaved changes check
    const handleSectionOverlayClick = (closeAction) => {
        if (hasSectionFormChanges()) {
            setPendingSectionCloseAction(() => closeAction);
            setShowDiscardSectionConfirm(true);
        } else {
            closeAction();
        }
    };

    // Confirm discard changes
    const confirmDiscard = () => {
        if (pendingCloseAction) {
            pendingCloseAction();
            setPendingCloseAction(null);
        }
        setShowDiscardConfirm(false);
        setInitialFormData(null);
    };

    // Cancel discard
    const cancelDiscard = () => {
        setShowDiscardConfirm(false);
        setPendingCloseAction(null);
    };

    // Confirm discard section changes
    const confirmSectionDiscard = () => {
        if (pendingSectionCloseAction) {
            pendingSectionCloseAction();
            setPendingSectionCloseAction(null);
        }
        setShowDiscardSectionConfirm(false);
        setInitialSectionFormData(null);
    };

    // Cancel section discard
    const cancelSectionDiscard = () => {
        setShowDiscardSectionConfirm(false);
        setPendingSectionCloseAction(null);
    };

    const showNotificationMessage = (type, message) => {
        setNotification({ show: true, type, message });
        setTimeout(() => {
            setNotification({ show: false, type: '', message: '' });
        }, 3000);
    };

    const handleAddGrade = async (e) => {
        e.preventDefault();
        if (readOnly) return;
        try {
            const maxOrder = grades.length > 0 ? Math.max(...grades.map(g => g.order_index)) : -1;

            const { error } = await supabase
                .from('grades')
                .insert([{
                    school_id: schoolId,
                    name: gradeFormData.name,
                    order_index: maxOrder + 1,
                    is_active: gradeFormData.is_active,
                    level: gradeFormData.level || 1
                }]);

            if (error) throw error;

            showNotificationMessage('success', 'Grade added successfully');
            setInitialFormData(null);
            setShowAddModal(false);
            setGradeFormData({ name: '', order_index: 0, is_active: true, level: 1 });
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
        }
    };

    const handleUpdateGrade = async (e) => {
        e.preventDefault();
        if (!editingGrade) return;
        if (readOnly) return;

        try {
            const { error } = await supabase
                .from('grades')
                .update({
                    name: gradeFormData.name,
                    order_index: gradeFormData.order_index,
                    is_active: gradeFormData.is_active,
                    level: gradeFormData.level || 1
                })
                .eq('id', editingGrade.id);

            if (error) throw error;

            showNotificationMessage('success', 'Grade updated successfully');
            setInitialFormData(null);
            setEditingGrade(null);
            setShowAddModal(false);
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
        }
    };

    const handleDeleteGrade = async () => {
        if (readOnly) return;
        if (!gradeToDelete) return;

        try {
            // First delete all sections for this grade
            const { error: sectionsError } = await supabase
                .from('grade_sections')
                .delete()
                .eq('grade_id', gradeToDelete);

            if (sectionsError) throw sectionsError;

            // Then delete the grade
            const { error } = await supabase
                .from('grades')
                .delete()
                .eq('id', gradeToDelete);

            if (error) throw error;

            showNotificationMessage('success', 'Grade deleted successfully');
            setShowDeleteGradeConfirm(false);
            setGradeToDelete(null);
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
            setShowDeleteGradeConfirm(false);
            setGradeToDelete(null);
        }
    };

    const confirmDeleteGrade = (gradeId) => {
        setGradeToDelete(gradeId);
        setShowDeleteGradeConfirm(true);
    };

    const cancelDeleteGrade = () => {
        setShowDeleteGradeConfirm(false);
        setGradeToDelete(null);
    };

    // Get subsections for a specific grade
    const getSectionsForGrade = (gradeId) => {
        return gradeSections.filter(section => section.grade_id === gradeId);
    };

    // Handle opening subsections modal
    const handleManageSubsections = (grade) => {
        if (readOnly) return;
        setSelectedGradeForSections(grade);
        setShowSubsectionsModal(true);
    };

    const openGradeStudents = (grade) => {
        setSelectedGradeForStudents(grade);
        setStudentsSearch('');
        setShowStudentsModal(true);
    };

    const closeGradeStudents = () => {
        setShowStudentsModal(false);
        setSelectedGradeForStudents(null);
        setStudentsInGrade([]);
        setStudentsLoading(false);
        setStudentsSearch('');
    };

    useEffect(() => {
        const fetchStudentsForGrade = async () => {
            if (!showStudentsModal || !selectedGradeForStudents?.id || !schoolId) return;
            setStudentsLoading(true);
            try {
                const { data, error } = await supabase
                    .from('users')
                    .select('id, user_id, first_name, last_name, full_name, email, grade, grade_id, grade_section_id, is_active, is_graduated')
                    .eq('school_id', schoolId)
                    .eq('role', 'student')
                    .eq('grade_id', selectedGradeForStudents.id)
                    .or('is_graduated.is.null,is_graduated.eq.false')
                    .order('last_name', { ascending: true });

                if (error) throw error;
                setStudentsInGrade(data || []);
            } catch (err) {
                showNotificationMessage('error', err.message);
                setStudentsInGrade([]);
            } finally {
                setStudentsLoading(false);
            }
        };

        fetchStudentsForGrade();
    }, [showStudentsModal, selectedGradeForStudents?.id, schoolId]);

    // Handle adding a new section
    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!selectedGradeForSections) return;
        if (readOnly) return;

        try {
            const sections = getSectionsForGrade(selectedGradeForSections.id);
            const maxOrder = sections.length > 0 ? Math.max(...sections.map(s => s.order_index)) : -1;

            const { error } = await supabase
                .from('grade_sections')
                .insert([{
                    grade_id: selectedGradeForSections.id,
                    name: sectionFormData.name,
                    order_index: maxOrder + 1,
                    is_active: sectionFormData.is_active
                }]);

            if (error) throw error;

            showNotificationMessage('success', 'Subsection added successfully');
            setInitialSectionFormData(null);
            setShowAddSectionModal(false);
            setSectionFormData({ name: '', order_index: 0, is_active: true });
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
        }
    };

    // Handle updating a section
    const handleUpdateSection = async (e) => {
        e.preventDefault();
        if (!editingSection) return;
        if (readOnly) return;

        try {
            const { error } = await supabase
                .from('grade_sections')
                .update({
                    name: sectionFormData.name,
                    order_index: sectionFormData.order_index,
                    is_active: sectionFormData.is_active
                })
                .eq('id', editingSection.id);

            if (error) throw error;

            showNotificationMessage('success', 'Subsection updated successfully');
            setInitialSectionFormData(null);
            setEditingSection(null);
            setShowAddSectionModal(false);
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
        }
    };

    // Handle deleting a section
    const handleDeleteSection = async () => {
        if (readOnly) return;
        if (!sectionToDelete) return;

        try {
            const { error } = await supabase
                .from('grade_sections')
                .delete()
                .eq('id', sectionToDelete);

            if (error) throw error;

            showNotificationMessage('success', 'Subsection deleted successfully');
            setShowDeleteSectionConfirm(false);
            setSectionToDelete(null);
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
            setShowDeleteSectionConfirm(false);
            setSectionToDelete(null);
        }
    };

    const confirmDeleteSection = (sectionId) => {
        setSectionToDelete(sectionId);
        setShowDeleteSectionConfirm(true);
    };

    const cancelDeleteSection = () => {
        setShowDeleteSectionConfirm(false);
        setSectionToDelete(null);
    };

    const fetchStudentPasswords = async (ids) => {
        const passwords = new Map();
        const chunkSize = 80;
        for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize).filter(Boolean);
            if (chunk.length === 0) continue;
            const { data, error } = await supabase
                .from('users')
                .select('id, password')
                .in('id', chunk);
            if (error) throw error;
            (data || []).forEach((row) => {
                passwords.set(row.id, row.password == null ? '' : String(row.password));
            });
        }
        return passwords;
    };

    const generateStudentExcel = async () => {
        if (!studentsInGrade || studentsInGrade.length === 0) {
            showNotificationMessage('error', 'No students to generate excel for');
            return;
        }
        if (pdfGenerating) return;

        setPdfGenerating(true);
        try {
            const passwords = await fetchStudentPasswords(studentsInGrade.map((student) => student.id));
            const gradeName = selectedGradeForStudents?.name || 'Grade';
            const cards = studentsInGrade.map((student) => ({
                name: student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unnamed',
                userId: student.user_id || '',
                password: passwords.get(student.id) || '',
                grade: gradeName,
            }));

            const logoDataUrl = await rasterizeLogo(logoMark);
            const buffer = await buildStudentLoginPdf(cards, logoDataUrl);
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${gradeName.replace(/\s+/g, '_')}_Student_Codes.pdf`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);

            const missing = cards.filter((card) => !card.userId || !card.password).length;
            if (missing > 0) {
                showNotificationMessage('success', `PDF generated. ${missing} card(s) have no QR because the ID or password is missing.`);
            } else {
                showNotificationMessage('success', 'PDF generated successfully');
            }
        } catch (err) {
            showNotificationMessage('error', err.message || 'Failed to generate PDF');
        } finally {
            setPdfGenerating(false);
        }
    };

    if (dataLoading) {
        return <div className="grades-loading">Loading grades...</div>;
    }

    return (
        <div className="dashboard-school-grades">
            {notification.show && (
                <div className={`grade-notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}

            <div className="grades-header">
                <h2>Grades Management</h2>
                {!readOnly && (
                    <button className="add-grade-btn" onClick={() => { setShowAddModal(true); setEditingGrade(null); setGradeFormData({ name: '', order_index: 0, is_active: true, level: 1 }); }}>
                        + Add Grade
                    </button>
                )}
            </div>

            {grades.length === 0 ? (
                <div className="no-grades">
                    <p>No grades found. Add your first grade to get started.</p>
                </div>
            ) : (
                <div className="grades-list">
                    {grades.map((grade) => (
                        <div key={grade.id} className="grade-card">
                            <div className="school-grade-header">
                                <h3>{grade.name}</h3>
                                <div className="grade-actions">
                                    <button className="view-students-btn" onClick={() => openGradeStudents(grade)}>
                                        View Students
                                    </button>
                                    {!readOnly && (
                                        <>
                                            <button className="dashboard-school-manage-btn" onClick={() => handleManageSubsections(grade)}>
                                                Manage Subsections
                                            </button>
                                            <button className="edit-btn" onClick={() => { setEditingGrade(grade); setGradeFormData(grade); setShowAddModal(true); }}>
                                                Edit
                                            </button>
                                            <button className="delete-btn" onClick={() => confirmDeleteGrade(grade.id)}>
                                                Delete
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="school-grade-info">
                                <span className={`grade-status ${grade.is_active ? 'active' : 'inactive'}`}>
                                    {grade.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <span className="section-count">
                                    {getSectionsForGrade(grade.id).length} subsections
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Students In Grade Modal */}
            {showStudentsModal && selectedGradeForStudents && (
                <div className="modal-overlay" onClick={closeGradeStudents}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <div className="grade-students-header">
                            <div>
                                <h3>Students in {selectedGradeForStudents.name}</h3>
                                <p className="grade-students-subtitle">
                                    {studentsLoading ? 'Loading…' : `${studentsInGrade.length} student(s)`}
                                </p>
                            </div>
                            <div className="modal-header-actions">
                                <button
                                    onClick={generateStudentExcel}
                                    disabled={studentsLoading || pdfGenerating || studentsInGrade.length === 0}
                                    className="generate-excel-btn"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M8 13H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M8 17H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    {pdfGenerating ? 'Generating…' : 'Generate PDF'}
                                </button>
                                <button className="grade-modal-close-btn" onClick={closeGradeStudents}>Close</button>
                            </div>
                        </div>

                        <div className="grade-students-search">
                            <input
                                type="text"
                                value={studentsSearch}
                                onChange={(e) => setStudentsSearch(e.target.value)}
                                placeholder="Search by name, email, or ID…"
                            />
                        </div>

                        {studentsLoading ? (
                            <div className="grades-loading">Loading students...</div>
                        ) : (
                            <div className="grade-students-list">
                                {(studentsInGrade || [])
                                    .filter((s) => {
                                        const q = studentsSearch.trim().toLowerCase();
                                        if (!q) return true;
                                        const name = (s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim()).toLowerCase();
                                        const email = String(s.email || '').toLowerCase();
                                        const uid = String(s.user_id || '').toLowerCase();
                                        return name.includes(q) || email.includes(q) || uid.includes(q);
                                    })
                                    .map((s) => (
                                        <div key={s.id} className="grade-student-row">
                                            <div className="grade-student-main">
                                                <div className="grade-student-name">{s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unnamed Student'}</div>
                                                <div className="grade-student-meta">
                                                    <span className="grade-student-id">{s.user_id || '—'}</span>
                                                    <span className="grade-student-email">{s.email || '—'}</span>
                                                </div>
                                            </div>
                                            <span className={`grade-student-status ${s.is_graduated
                                                ? 'graduated'
                                                : s.is_active
                                                    ? 'active'
                                                    : 'inactive'
                                                }`}>
                                                {s.is_graduated ? 'Graduated' : s.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    ))}

                                {!studentsInGrade?.length && (
                                    <div className="no-sections">No students found for this grade.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Discard Changes Confirmation Dialog */}
            {showDiscardConfirm && (
                <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
                    <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="unsaved-changes-header">
                            <div className="unsaved-changes-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                </svg>
                            </div>
                            <h2>Unsaved Changes</h2>
                        </div>
                        <div className="unsaved-changes-body">
                            <p className="unsaved-changes-message">
                                You have unsaved changes. Are you sure you want to discard them?
                            </p>
                        </div>
                        <div className="unsaved-changes-actions">
                            <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                                Cancel
                            </button>
                            <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                                Discard Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Grade Modal */}
            {!readOnly && showAddModal && (
                <div className="modal-overlay" onClick={() => handleOverlayClick(() => setShowAddModal(false))}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingGrade ? 'Edit Grade' : 'Add New Grade'}</h3>
                        <form onSubmit={editingGrade ? handleUpdateGrade : handleAddGrade}>
                            <div className="form-group">
                                <label>Grade Name *</label>
                                <input
                                    type="text"
                                    value={gradeFormData.name}
                                    onChange={(e) => setGradeFormData({ ...gradeFormData, name: e.target.value })}
                                    required
                                    placeholder="e.g., Grade 1, Grade 2, etc."
                                />
                            </div>
                            <div className="form-group">
                                <label>Level (1-12)</label>
                                <input
                                    type="number"
                                    value={gradeFormData.level || 1}
                                    onChange={(e) => setGradeFormData({ ...gradeFormData, level: parseInt(e.target.value) || 1 })}
                                    min="1"
                                    max="12"
                                    placeholder="Grade level for SSO providers"
                                />
                            </div>
                            {editingGrade && (
                                <>
                                    {/* Order Index hidden from UI
                                    <div className="form-group">
                                        <label>Order Index</label>
                                        <input
                                            type="number"
                                            value={gradeFormData.order_index}
                                            onChange={(e) => setGradeFormData({ ...gradeFormData, order_index: parseInt(e.target.value) })}
                                            min="0"
                                        />
                                    </div>
                                    */}
                                    <div className="form-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={gradeFormData.is_active}
                                                onChange={(e) => setGradeFormData({ ...gradeFormData, is_active: e.target.checked })}
                                            />
                                            Active
                                        </label>
                                    </div>
                                </>
                            )}
                            <div className="form-actions">
                                <button type="button" className="cancel-btn" onClick={() => handleOverlayClick(() => setShowAddModal(false))}>Cancel</button>
                                <button type="submit" className="submit-btn">{editingGrade ? 'Update' : 'Add'} Grade</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Subsections Management Modal */}
            {!readOnly && showSubsectionsModal && selectedGradeForSections && (
                <div className="modal-overlay" onClick={() => setShowSubsectionsModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <h3>Manage Subsections for {selectedGradeForSections.name}</h3>
                        <button className="add-section-btn" onClick={() => { setShowAddSectionModal(true); setEditingSection(null); setSectionFormData({ name: '', order_index: 0, is_active: true }); }}>
                            + Add Subsection
                        </button>
                        <div className="sections-list">
                            {getSectionsForGrade(selectedGradeForSections.id).length === 0 ? (
                                <p className="no-sections">No subsections found. Add your first subsection.</p>
                            ) : (
                                getSectionsForGrade(selectedGradeForSections.id).map((section) => (
                                    <div key={section.id} className="section-item">
                                        <div className="section-info">
                                            <span className="section-name">{section.name}</span>
                                            <span className={`section-status ${section.is_active ? 'active' : 'inactive'}`}>
                                                {section.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="section-actions">
                                            <button className="edit-btn" onClick={() => { setEditingSection(section); setSectionFormData(section); setShowAddSectionModal(true); }}>
                                                Edit
                                            </button>
                                            <button className="delete-btn" onClick={() => confirmDeleteSection(section.id)}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <button className="close-modal-btn" onClick={() => setShowSubsectionsModal(false)}>Close</button>
                    </div>
                </div>
            )}

            {/* Discard Section Changes Confirmation Dialog */}
            {showDiscardSectionConfirm && (
                <div className="unsaved-changes-overlay" onClick={cancelSectionDiscard}>
                    <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="unsaved-changes-header">
                            <div className="unsaved-changes-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                </svg>
                            </div>
                            <h2>Unsaved Changes</h2>
                        </div>
                        <div className="unsaved-changes-body">
                            <p className="unsaved-changes-message">
                                You have unsaved changes. Are you sure you want to discard them?
                            </p>
                        </div>
                        <div className="unsaved-changes-actions">
                            <button className="unsaved-changes-cancel-btn" onClick={cancelSectionDiscard}>
                                Cancel
                            </button>
                            <button className="unsaved-changes-discard-btn" onClick={confirmSectionDiscard}>
                                Discard Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Subsection Modal */}
            {!readOnly && showAddSectionModal && (
                <div className="modal-overlay" onClick={() => handleSectionOverlayClick(() => setShowAddSectionModal(false))}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingSection ? 'Edit Subsection' : 'Add New Subsection'}</h3>
                        <form onSubmit={editingSection ? handleUpdateSection : handleAddSection}>
                            <div className="form-group">
                                <label>Subsection Name *</label>
                                <input
                                    type="text"
                                    value={sectionFormData.name}
                                    onChange={(e) => setSectionFormData({ ...sectionFormData, name: e.target.value })}
                                    required
                                    placeholder="e.g., Section A, Section B, etc."
                                />
                            </div>
                            {editingSection && (
                                <>
                                    <div className="form-group">
                                        <label>Order Index</label>
                                        <input
                                            type="number"
                                            value={sectionFormData.order_index}
                                            onChange={(e) => setSectionFormData({ ...sectionFormData, order_index: parseInt(e.target.value) })}
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={sectionFormData.is_active}
                                                onChange={(e) => setSectionFormData({ ...sectionFormData, is_active: e.target.checked })}
                                            />
                                            Active
                                        </label>
                                    </div>
                                </>
                            )}
                            <div className="form-actions">
                                <button type="button" className="cancel-btn" onClick={() => handleSectionOverlayClick(() => setShowAddSectionModal(false))}>Cancel</button>
                                <button type="submit" className="submit-btn">{editingSection ? 'Update' : 'Add'} Subsection</button>
                            </div>
                        </form>
                    </div>
            </div>
        )}

        {/* Delete Grade Confirmation Dialog */}
        {showDeleteGradeConfirm && (
            <div className="dashboard-school-grades-delete-overlay" onClick={cancelDeleteGrade}>
                <div className="dashboard-school-grades-delete-dialog" onClick={(e) => e.stopPropagation()}>
                    <div className="dashboard-school-grades-delete-header">
                        <div className="dashboard-school-grades-delete-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                            </svg>
                        </div>
                        <h2>Delete Grade</h2>
                    </div>
                    <div className="dashboard-school-grades-delete-body">
                        <p className="dashboard-school-grades-delete-message">
                            Are you sure you want to delete this grade? This will also delete all subsections for this grade. This action cannot be undone.
                        </p>
                    </div>
                    <div className="dashboard-school-grades-delete-actions">
                        <button className="dashboard-school-grades-delete-cancel-btn" onClick={cancelDeleteGrade}>
                            Cancel
                        </button>
                        <button className="dashboard-school-grades-delete-confirm-btn" onClick={handleDeleteGrade}>
                            Delete Grade
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Delete Section Confirmation Dialog */}
        {showDeleteSectionConfirm && (
            <div className="dashboard-school-sections-delete-overlay" onClick={cancelDeleteSection}>
                <div className="dashboard-school-sections-delete-dialog" onClick={(e) => e.stopPropagation()}>
                    <div className="dashboard-school-sections-delete-header">
                        <div className="dashboard-school-sections-delete-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                            </svg>
                        </div>
                        <h2>Delete Subsection</h2>
                    </div>
                    <div className="dashboard-school-sections-delete-body">
                        <p className="dashboard-school-sections-delete-message">
                            Are you sure you want to delete this subsection? This action cannot be undone.
                        </p>
                    </div>
                    <div className="dashboard-school-sections-delete-actions">
                        <button className="dashboard-school-sections-delete-cancel-btn" onClick={cancelDeleteSection}>
                            Cancel
                        </button>
                        <button className="dashboard-school-sections-delete-confirm-btn" onClick={handleDeleteSection}>
                            Delete Subsection
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
);
};

export default DashboardSchoolGrades;
